/* global tracking */
'use strict';
const get = require('lodash/get');
const isEmpty = require('lodash/isEmpty');
const debug = require('debug')('webstore:track');
const LocalStorage = require('public/js/lib/local-storage');
const SearchAttribution = require('public/js/lib/search-attribution');
const {
	STEER_CONTENT_GROUP,
	buildSteerGtagPageContext,
	isSteerExperiencePath,
	readGa4ClientIdFromCookie,
} = require('public/js/lib/steer-ga4');
const utils = require('public/js/helpers/utils'); 

const accountGroups = {
	ecEnabled: 'ecEnabled',
	ecDisabled: 'ecDisabled',
	customer: 'customer',
	rp: 'rp',
	default: 'default'
};

const customDimensions = {
	domain: 'Domain',
	hash: 'Store Hash',
	isLoggedIn: 'Logged In User',
	carSelected: 'Car Selected',
	storeType: 'Store Type',
	stockLevelMessaging: 'Stock Level Messaging'
};

const accountIds = {
	rollup: 'rollup',
	virtual: 'virtual',
	rpCust: 'rpcust'
};

const legacyCartEvents = {
	add: 'add',
	remove: 'remove',
	addToWishlist: 'add to wishlist',
};

const legacyVisualizerEvents = {
	click: 'click',
	launchError: 'launch_error',
};

const gtagEvents = {
	addToCart: 'add_to_cart',
	addToWishlist: 'add_to_wishlist',
	removeFromCart: 'remove_from_cart',
	selectContent: 'select_content',
	viewItem: 'view_item',
	viewItemList: 'view_item_list',
	viewSearchResults: 'view_search_results',
	purchase: 'purchase',
	contactUs: 'contact_us',
	webSearch: 'web_search',
	crossSellAtc: 'cross_sell_atc',
	visualizerView: 'visualizer_view',
	visualizerError: 'visualizer_error',
};

/**
 * @param {number|string|null|undefined} value
 * @returns {number}
 */
function normalizeSearchResultCount(value) {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return Math.max(0, Math.floor(value));
	}

	if (typeof value === 'string' && value.length > 0) {
		const parsed = Number.parseInt(value.replace(/,/g, ''), 10);

		return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
	}

	return 0;
}

/**
 * @returns {string|null}
 */
function getStoreHashForAnalytics() {
	const hash = get(tracking, 'dimensions.hash');

	return typeof hash === 'string' && hash.length > 0 ? hash : null;
}

/**
 * Attach originating search_term (when present) and store_hash to a GA4 event payload.
 *
 * @param {Record<string, *>} eventDetails
 * @param {{ includeSearchTerm?: boolean }} [options]
 * @returns {Record<string, *>}
 */
function attachSearchAttribution(eventDetails, options) {
	const details = eventDetails || {};
	const includeSearchTerm = !options || options.includeSearchTerm !== false;
	const storeHash = getStoreHashForAnalytics();

	if (storeHash !== null && typeof details.store_hash !== 'string') {
		details.store_hash = storeHash;
	}

	if (includeSearchTerm) {
		const searchTerm = SearchAttribution.readOriginatingTerm();
		if (searchTerm !== null) {
			details.search_term = searchTerm;
		}
	}

	return details;
}

/**
 * @param {Array<Record<string, *>>} products
 * @param {(item: Record<string, *>) => Record<string, *>} mapItem
 * @returns {Array<Record<string, *>>}
 */
function mapProductsWithSearchTerm(products, mapItem) {
	return products.map((item) => {
		const mapped = mapItem(item);
		if (typeof item.search_term === 'string' && item.search_term.length > 0) {
			const masked = SearchAttribution.maskTerm(item.search_term);
			if (masked.length > 0) {
				mapped.search_term = masked;
			}
		}

		return mapped;
	});
}

const track = {
	debug: false,
	trackers: {},
	transaction: null
};

var _gaq = _gaq || [];

track.init = function () {
	const dimensions = tracking.dimensions;
	const storeDimensions = {
		car_details: utils.buildCarString(dimensions.carDetails ?? {}),
		car_selected: dimensions.carSelected,
		domain: dimensions.domain,
		logged_in_user: dimensions.isLoggedIn,
		store_hash: dimensions.hash,
		...buildSteerGtagPageContext(window.location.pathname),
	};

	// CUSTOMER ACCOUNTS
	if (tracking.google.analytics_4_ua_numbers) {
		tracking.google.analytics_4_ua_numbers.forEach((trackNumber) => {
			let groups = `default,${accountGroups.customer}`;

			if (tracking.google.enhanced_ecommerce_ga_4_enabled) {
				groups += `,${accountGroups.ecEnabled}`;
			} else {
				groups += `,${accountGroups.ecDisabled}`;
			}

			globalThis.gtag('config', trackNumber, {
				groups,
				send_page_view: false,
				...storeDimensions,
				...(!tracking.google.use_gtag_admind_analytics && { anonymize_ip: true }),
			});
		});
	}


	// RP ACCOUNTS
	if(tracking.google.rp_ga4_numbers) {
		tracking.google.rp_ga4_numbers.forEach((rpTrackNumber) => {
			// Initialize RP Account
			const accountId = rpTrackNumber.id;
			let groups = `default,${accountGroups.rp},${accountId}`;

			// Always enable EC for rollup/virtual. Only enable for rpcust if rp_enhanced_ecommerce_enabled is true.
			if (tracking.google.rp_enhanced_ecommerce_ga_4_enabled || (accountId === 'rollup' || accountId === 'virtual')) {
				groups += `,${accountGroups.ecEnabled}`;
			} else {
				groups += `,${accountGroups.ecDisabled}`;
			}

			globalThis.gtag('config', rpTrackNumber.ua, {
				custom_map: track.get_custom_dimensions_map(accountId),
				groups,
				send_page_view: false,
				store_type: rp_app.env.is_webstore_plugin ? 'Webstore_Plugin' : 'Webstore',
				...storeDimensions,
				...(!tracking.google.use_gtag_admind_analytics && { anonymize_ip: true }),
			});
		});
	}

	// ADWORDS ENHANCED CONVERSIONS
	if (tracking.google.adwords_id) {
		const adwordsId = 'AW-' + tracking.google.adwords_id;
		globalThis.gtag('config', adwordsId, {
			allow_enhanced_conversions: true,
			send_page_view: false
		});

		this.sendAdwordsRemarketing();
	}

	globalThis.gtag('set', this.get_custom_dimensions());
};

/**
 * Send the Google Ads dynamic remarketing event for the current page.
 *
 * Deliberately not gated on google_analytics_enabled: remarketing belongs to Google Ads,
 * not Analytics, and gating it there would drop remarketing for GA-disabled stores.
 *
 * Truthiness checks mirror the retired Smarty template exactly. An empty products array
 * still sends ecomm_prodid, and a zero value still omits ecomm_totalvalue, because Google
 * Ads audience lists were built against that behavior.
 */
track.sendAdwordsRemarketing = function () {
	const remarketingDetails = {
		send_to: 'AW-' + tracking.google.adwords_id,
		ecomm_pagetype: tracking.google.page_type,
	};

	if (tracking.value) {
		remarketingDetails.ecomm_totalvalue = tracking.value;
	}

	if (tracking.products) {
		remarketingDetails.ecomm_prodid = tracking.products.map((product) => product.product_id);
	}

	if (tracking.category) {
		remarketingDetails.ecomm_category = tracking.category;
	}

	globalThis.gtag('event', 'page_view', remarketingDetails);

	debug('sendAdwordsRemarketing', remarketingDetails);
};

/**
 * Send Adwords Enhanced Conversion event with hashed PII
 * @param {Object} params - Parameters for the enhanced conversion
 * @param {Object} params.transaction - Transaction object containing PII (buyer_email_address, buyer_phone_number, buyer_name, etc.)
 * @param {Array} params.items - Array of item objects for the purchase
 * @param {string} params.transaction_id - The transaction/order ID
 * @param {number} params.value - The transaction total value
 * @param {string} params.currency - The currency code
 * @param {number} params.tax - Tax amount
 * @param {number} params.shipping - Shipping amount
 * @param {number} params.discount - Discount amount
 * @param {string} params.channel_order_number - Channel order number (optional)
 * @param {string} params.debugSource - Source identifier for debug logging
 * @returns {Promise<void>} Resolves once the conversion has been sent, with or without hashed user_data.
 */
track.sendAdwordsEnhancedConversion = function(params) {
	const {
		transaction,
		items,
		transaction_id,
		value,
		currency,
		tax,
		shipping,
		discount,
		channel_order_number,
		debugSource
	} = params;

	const enhancedAdwordsEnabled = window.tracking.google.adwords_id && window.tracking.google.adwords_label;
	if (!enhancedAdwordsEnabled) {
		return Promise.resolve();
	}

	const adwordsId = 'AW-' + window.tracking.google.adwords_id;
	const adwordsLabel = window.tracking.google.adwords_label;
	const adwordsTarget = `${adwordsId}/${adwordsLabel}`;

	// Split buyer_name into first and last name for Enhanced Conversions
	const nameParts = (transaction.buyer_name || '').trim().split(/\s+/);
	const firstName = nameParts[0] || '';
	const lastName = nameParts.slice(1).join(' ') || '';

	return Promise.all([
		utils.hashSensitiveInformation(transaction.buyer_email_address),
		utils.hashSensitiveInformation(transaction.buyer_phone_number),
		utils.hashSensitiveInformation(firstName),
		utils.hashSensitiveInformation(lastName),
		utils.hashSensitiveInformation(transaction.buyer_address),
	]).catch(function (error) {
		// hashSensitiveInformation needs crypto.subtle, which is absent outside a secure context.
		// The conversion still fires without user_data so measurement matches the retired Adwords pixel.
		rp_app.logger.error({
			message: `ADWORDS_ENHANCED_CONVERSION_HASH_FAILED id: ${transaction_id}`,
			debugSource: debugSource,
			error: error.message,
		});

		return null;
	}).then(function (hashedValues) {
		// Transform items to Google Ads cart data format
		// GA4 uses: item_id, item_name, price, quantity
		// Google Ads expects: id, price, quantity
		const adwordsItems = items.map(function(item) {
			return {
				id: item.item_id || item.id,
				price: item.price,
				quantity: item.quantity
			};
		});

		const conversionDetails = {
			send_to: adwordsTarget,
			transaction_id: transaction_id,
			value: value,
			currency: currency,
			tax: tax,
			shipping: shipping,
			discount: discount || 0,
			channel_order_number: channel_order_number || undefined,
			items: adwordsItems,
			aw_merchant_id: window.tracking.google.merchant_center_id || undefined,
			aw_feed_language: transaction.language || undefined,
			aw_feed_country: transaction.country || undefined,
		};

		if (hashedValues) {
			// Per Google's Enhanced Conversions spec (https://developers.google.com/google-ads/api/docs/conversions/upload-online):
			// - email, phone, first_name, last_name, street_address: MUST be SHA-256 hashed
			// - city, region, postal_code, country: MUST be sent as plain text (not hashed)
			// delve-disable-next-line information-disclosure
			conversionDetails.user_data = {
				sha256_email_address: hashedValues[0],
				sha256_phone_number: hashedValues[1],
				address: {
					sha256_first_name: hashedValues[2],
					sha256_last_name: hashedValues[3],
					sha256_street_address: hashedValues[4],
					city: transaction.buyer_city,
					region: transaction.buyer_state,
					postal_code: transaction.buyer_postal_code,
					country: transaction.buyer_country || transaction.country,
				}
			};
		}

		globalThis.gtag('event', 'conversion', conversionDetails);

		const conversionType = hashedValues ? 'Enhanced Conversion' : 'Conversion without user_data';
		const conversionSource = debugSource ? ` from ${debugSource}` : '';

		debug(`Sent Adwords ${conversionType} event${conversionSource}`, adwordsTarget);
	});
};

track.track_event = function (event_args, options, name) {
	let groupName = name;

	// Enhanced Commerce setting is irrelevant for gtag -- based on what is set on Analytics platform
	if (groupName === 'all') {
		groupName = accountGroups.default;
	} else if (groupName === 'ec') {
		groupName = accountGroups.ecEnabled;
	}

	options = options || {};
	if (!options.event_category) {
		options.event_category = event_args[0];
	}
	if (!options.event_action) {
		options.event_action = event_args[1];
	}
	if (!options.event_label) {
		options.event_label = event_args[2];
	}

	if (options.nonInteraction) {
		options.non_interaction = true;
	}

	//const customDimensions = this.get_custom_dimensions();
	//options = {...options, ...customDimensions};
	if (groupName) {
		options.send_to = groupName;
	}

	globalThis.gtag('event', options.event_action, options);

	debug('track_event', groupName, event_args, options);
};

track.track_transaction = function (name) {
	let groupName = null;

	if (name === 'all' || !name) {
		groupName = accountGroups.ecDisabled;
	}

	let transaction;
	transaction = tracking.transaction;
	if(!transaction) { return; }

	const items = mapProductsWithSearchTerm(tracking.products, (item) => ({
		item_id: item.product_id,     // Item ID.
		item_name: item.title,
		item_category: item.category || '',
		price: item.price,
		quantity: item.quantity
	}));

	let options = {
		channel_order_number: transaction.channel_order_number || undefined,
		currency: transaction.currency_code,
		transaction_id: transaction.order_number,  	// Transaction ID.
		value: transaction.total,										// Grand Total.
		affiliation: transaction.affiliation,				// Affiliation or store name.
		shipping: transaction.shipping,							// Shipping.
		tax: transaction.tax, 											// Tax.
		items,
	};

	if (groupName) {
		options.send_to = groupName;
	}

	attachSearchAttribution(options, { includeSearchTerm: false });

	// Send GA4 purchase event (without PII)
	globalThis.gtag('event', gtagEvents.purchase, options);

	// Send Adwords Enhanced Conversion event with PII
	if (window.tracking.google.adwords_id) {
		this.sendAdwordsEnhancedConversion({
			transaction: transaction,
			items: items,
			transaction_id: transaction.order_number,
			value: transaction.total,
			currency: transaction.currency_code,
			tax: transaction.tax,
			shipping: transaction.shipping,
			discount: transaction.discount,
			channel_order_number: transaction.channel_order_number,
			debugSource: 'track_transaction'
		});
	}

	debug('track_transaction complete', groupName);
};

track.track_pageview = function (name) {
	const groupName = name === 'all' ? 'default' : name;

	let pageViewDetails = {
		page_location: window.location.href,
		page_path: window.location.pathname,
	};

	if (isSteerExperiencePath(window.location.pathname)) {
		pageViewDetails.content_group = STEER_CONTENT_GROUP;
		const visitorId = readGa4ClientIdFromCookie();
		if (visitorId !== '') {
			pageViewDetails.visitor_id = visitorId;
		}
	}

	if (groupName) {
		pageViewDetails.send_to = groupName;
	}

	if (rp_app.env.google_analytics_enabled) {
		globalThis.gtag('event', 'page_view', pageViewDetails);
		this.do_ec_product_tracking();
	}
};

track.track_pageview_all_trackers = function() {
	this.track_pageview(accountGroups.default, tracking.page);
};

track.track_ec_impressions = function() {
	const products = tracking.ec.products;
	if (rp_app.env.google_analytics_enabled && Array.isArray(products)) {
		const itemListEventDetails = {
			number_of_results:products.length,
			items: tracking.google.use_gtag_admind_analytics?utils.convertNamesOfItemsForGA(products):products,
			send_to: accountGroups.ecEnabled
		};

		globalThis.gtag('event', gtagEvents.viewItemList, itemListEventDetails);
		debug('track_ec_impressions', tracking.ec.products);
	}
};

track.track_ec_cart_event = function(eventName, product_details, currency_cart = 'USD') {
	if (rp_app.env.google_analytics_enabled) {
		const products = Array.isArray(product_details) ? product_details : [product_details];
		const isProductPage = (tracking.google.page_type === 'product');
		const cartEventDetails = { items: products };
		const gtagEvent = this.get_equivalent_gtag_event(eventName);
		const eventArgs = ['EnhancedEcommerce', gtagEvent, `cart_${eventName}`];
		cartEventDetails.currency = currency_cart;
		cartEventDetails.send_to = accountGroups.ecEnabled;

		if (isProductPage) {
			// Add the inventory messaging key if it is set, otherwise use default value
			cartEventDetails[customDimensions.stockLevelMessaging] =
				get(tracking, 'dimensions.stockLevelMessaging', 'NoInventoryMessaging');
		}

		if (gtagEvent === gtagEvents.addToCart || gtagEvent === gtagEvents.purchase) {
			attachSearchAttribution(cartEventDetails);
		}

		this.track_event(eventArgs, cartEventDetails);

		debug('track_ec_cart_event', eventName, products);
	}
};

track.track_ec_general = function(eventName, data) {
	if (rp_app.env.google_analytics_enabled) {
		const dataDetails = data;
		const gtagEvent = this.get_equivalent_gtag_event(eventName);
		const eventArgs = ['EnhancedEcommerce', gtagEvent, eventName];
		dataDetails.send_to = accountGroups.ecEnabled;
		dataDetails.page_type = tracking.google.page_type;

		if (eventName === gtagEvents.webSearch) {
			const query = typeof data.query === 'string' ? data.query.trim() : '';
			const queryUpper = query.toUpperCase();
			let searchType = 'keyword';
			let isValidQuery = query.length > 0;

			// VIN match is case-insensitive; part-number regex stays uppercase-only.
			if (SearchAttribution.VIN_PATTERN.test(queryUpper)) {
				searchType = 'VIN';
			} else if (/^[A-Z0-9-]+$/.test(query)) {
				searchType = 'part number';
			}

			dataDetails.search_type = searchType;
			dataDetails.is_valid_query = isValidQuery;
			// Result metrics belong on view_search_results (SERP), not submit.
			delete dataDetails.number_of_results;
			delete dataDetails.search_term;
			delete dataDetails.zero_results;

			if (searchType === 'VIN') {
				dataDetails.query = 'VIN search';
			} else {
				dataDetails.query = query;
			}

			const storeHash = getStoreHashForAnalytics();
			if (storeHash !== null) {
				dataDetails.store_hash = storeHash;
			}
		}

		if (eventName === gtagEvents.addToCart || eventName === gtagEvents.purchase) {
			attachSearchAttribution(dataDetails, {
				includeSearchTerm: eventName !== gtagEvents.purchase,
			});
		}

		this.track_event(eventArgs, dataDetails);
		debug('track_ec_general', eventName, dataDetails);
	}
};

track.track_steer_ga4_event = function(eventName, data) {
	if (rp_app.env.google_analytics_enabled) {
		const dataDetails = data;
		dataDetails.send_to = accountGroups.ecEnabled;
		dataDetails.page_type = tracking.google.page_type;
		this.track_event(['EnhancedEcommerce', eventName, eventName], dataDetails);
		debug('track_steer_ga4_event', eventName, dataDetails);
	}
};

track.track_ec_contact_us = function(eventName, data) {
	if (rp_app.env.google_analytics_enabled) {
		const dataDetails = data;
		const gtagEvent = this.get_equivalent_gtag_event(eventName);
		const eventArgs = ['EnhancedEcommerce', gtagEvent];
		dataDetails.send_to = accountGroups.ecEnabled;
		this.track_event(eventArgs, dataDetails);
		debug('track_ec_contact_us', eventName, dataDetails);
	}
};

track.page_tracking = function (pageType) {
	if(pageType === 'checkout/completed' && get(tracking, 'transaction.track_transaction')) {
		this.track_transaction('all');
	}

	this.track_view_search_results();
};

/**
 * Fire enriched GA4 view_search_results once per search-results page load.
 *
 * @returns {void}
 */
track.track_view_search_results = function() {
	const searchTerm = get(tracking, 'dimensions.search_term');
	if (typeof searchTerm !== 'string' || searchTerm.length === 0) {
		return;
	}

	if (!get(globalThis, 'rp_app.env.google_analytics_enabled')) {
		return;
	}

	const resultCount = normalizeSearchResultCount(get(tracking, 'dimensions.search_result_count'));

	if (tracking.dimensions) {
		delete tracking.dimensions.search_term;
		delete tracking.dimensions.search_result_count;
	}

	const maskedTerm = SearchAttribution.maskTerm(searchTerm);

	const trimmedTerm = searchTerm.trim();
	const searchTermUpper = trimmedTerm.toUpperCase();
	let searchType = 'keyword';
	if (SearchAttribution.VIN_PATTERN.test(searchTermUpper)) {
		searchType = 'VIN';
	} else if (/^[A-Z0-9-]+$/.test(trimmedTerm)) {
		searchType = 'part number';
	}

	/** @type {Record<string, *>} */
	const eventDetails = {
		is_valid_query: true,
		number_of_results: resultCount,
		query: maskedTerm,
		search_term: maskedTerm,
		search_type: searchType,
		send_to: accountGroups.ecEnabled,
		zero_results: resultCount === 0,
	};

	const storeHash = getStoreHashForAnalytics();
	if (storeHash !== null) {
		eventDetails.store_hash = storeHash;
	}

	globalThis.gtag('event', gtagEvents.viewSearchResults, eventDetails);
	debug('track_view_search_results', eventDetails);
};

track.do_ec_product_tracking = function() {
	// If tracking.ec.product_detail is in use, we should be on a product page and so we need to send product details
	if (!isEmpty(tracking.ec.product_detail)) {
		// If tracking.ec.product_click is in use, we should also track the click that got us to the product page
		// NOTE: We want to catch this first so we can send out the click event separately from the details event
		if (!isEmpty(tracking.ec.product_click)) {
			this.track_ec_product_click();
		}

		this.track_ec_product_details();
	}

	// If tracking.ec.products is in use, that means impressions should have occurred for each of those products
	if (!isEmpty(tracking.ec.products)) {
		this.track_ec_impressions();
	}

	// Construct a variety of checks so we are extra-certain we want to do the final purchase analytics
	const transaction_id = get(tracking, 'ec.transaction_details.id');
	const storage_key = `analytics:order_completed:${transaction_id}`;
	const has_tracking_data = (!isEmpty(get(tracking, 'ec.completed_cart_products'))
		&& !isEmpty(get(tracking, 'ec.transaction_details')));
	const is_completed_page = (tracking.page === 'checkout/completed');
	const analytics_completed = (LocalStorage.getItem(storage_key) !== null);
	const track_transaction = get(tracking, 'transaction.track_transaction');
	const should_do_order_completed_analytics = has_tracking_data
		&& is_completed_page
		&& !analytics_completed
		&& track_transaction;

	debug('Pre-purchase analytics checks', {
		has_tracking_data,
		is_completed_page,
		analytics_completed,
		track_transaction,
		should_do_order_completed_analytics,
		storage_key,
	});

	// If all of the checks pass, fire the relevant analytics function
	if (should_do_order_completed_analytics) {
		// KT-2536: Adding some logs here so we can get more information if duplicate order tracking happens
		rp_app.logger.error({
			message: `ORDER_ANALYTICS_TRACKING id: ${transaction_id}`,
			total_quantity: get(tracking, 'transaction.total_quantity'),
			transaction_id: transaction_id,
		});

		this.track_ec_complete_transaction();
		LocalStorage.setItem(storage_key, true);
	}
};

track.track_ec_complete_transaction = function() {
	const items = mapProductsWithSearchTerm(tracking.ec.completed_cart_products, (item) => ({
		item_id: item.id,     // Item ID.
		item_name: item.name,
		item_brand: item.brand,
		price: item.price,
		quantity: item.quantity
	}));

	const transactionDetails = get(tracking, 'ec.transaction_details', {});
	const transaction = get(tracking, 'transaction', {});

	const purchaseDetails = {
		currency: transactionDetails.currency ? transactionDetails.currency : 'USD',
		transaction_id: transactionDetails.id,
		value: transactionDetails.revenue,
		shipping: transactionDetails.shipping,
		tax: transactionDetails.tax,
		discount: transactionDetails.discount,
		channel_order_number: transaction.channel_order_number || undefined,
		send_to: accountGroups.ecEnabled,
		items
	};

	attachSearchAttribution(purchaseDetails, { includeSearchTerm: false });

	// Send GA4 purchase event (without PII)
	const event_args = ['EnhancedEcommerce', gtagEvents.purchase, 'Purchase'];
	this.track_event(event_args, purchaseDetails);
	debug('Sent GA4 purchase event from track_ec_complete_transaction');

	if (window.tracking.google.adwords_id) {
		this.sendAdwordsEnhancedConversion({
			transaction: transaction,
			items: items,
			transaction_id: transactionDetails.id,
			value: transactionDetails.revenue,
			currency: transactionDetails.currency || 'USD',
			tax: transactionDetails.tax,
			shipping: transactionDetails.shipping,
			discount: transactionDetails.discount,
			channel_order_number: transaction.channel_order_number,
			debugSource: 'track_ec_complete_transaction'
		});
	}

	debug('track_ec_complete_transaction complete', tracking.ec.completed_cart_products, tracking.ec.transaction_details);
};

track.track_ec_product_click = function() {
	if (get(window, 'tracking.google.use_gtag_admind_analytics')) {
		return;
	}
	const list = get(tracking, 'ec.product_click.list');
	const position = get(tracking, 'ec.product_click.position');

	if (list && position) {
		const details = {
			content_type: 'product',
			send_to: accountGroups.ecEnabled,
			non_interaction: true,
			items: [
				{
					...tracking.ec.product_detail,
					list_position: position,
					list_name: list
				}
			]
		};

		// Send the click event
		const event_args = ['EnhancedEcommerce', gtagEvents.selectContent, 'ProductClick'];
		this.track_event(event_args, details);

		debug('track_ec_product_click', tracking.ec.product_click);
	}
};

track.track_ec_product_details = function() {
	const list = get(tracking, 'ec.product_click.list');
	const position = get(tracking, 'ec.product_click.position');
	const items = [
		{
			...tracking.ec.product_detail,
			list_position: position,
			list_name: list
		}
	];

	const details = {
		send_to: accountGroups.ecEnabled,
		items: tracking.google.use_gtag_admind_analytics?utils.convertNamesOfItemsForGA(items):items,
	};

	// Send the details event
	const event_args = ['EnhancedEcommerce', gtagEvents.viewItem, 'ProductDetails'];
	this.track_event(event_args, details);

	debug('track_ec_product_details', tracking.ec.product_detail);
};

track.track_ec_visualizer_event = function(eventName, data) {
	if (rp_app.env.google_analytics_enabled) {
		const visualizerEventDetails = { ...data, send_to: accountGroups.ecEnabled};

		const gtagEvent = this.get_equivalent_visualizer_gtag_event(eventName);
		const eventArgs = ['EnhancedEcommerce', gtagEvent, `visualizer_${eventName}`];
		this.track_event(eventArgs, visualizerEventDetails);

		debug('track_ec_visualizer_event', eventName, eventArgs, visualizerEventDetails);
	}
};

track.get_custom_dimensions = function () {
	const storeType = (rp_app.env.is_webstore_plugin) ? 'Webstore_Plugin' : 'Webstore';

	return {
		[customDimensions.domain]: tracking.dimensions.domain,
		[customDimensions.hash]: tracking.dimensions.hash,
		[customDimensions.isLoggedIn]: tracking.dimensions.isLoggedIn,
		[customDimensions.carSelected]: tracking.dimensions.carSelected,
		[customDimensions.storeType]: storeType
	};
};

track.get_custom_dimensions_map = function (accountId) {
	let customDimensionsMap = null;

	switch (accountId) {
		case accountIds.rollup:
		case accountIds.virtual:
			customDimensionsMap = {
				'dimension1': customDimensions.domain,
				'dimension2': customDimensions.hash,
				'dimension3': customDimensions.carSelected,
				'dimension4': customDimensions.isLoggedIn,
				'dimension5': customDimensions.storeType,
				'dimension7': customDimensions.stockLevelMessaging
			};
			break;
		default:
			customDimensionsMap = {};
	}

	return customDimensionsMap;
};

track.get_equivalent_gtag_event = function(eventName) {
	let gtagEventName = null;

	switch (eventName) {
		case legacyCartEvents.add:
			gtagEventName = gtagEvents.addToCart;
			break;
		case legacyCartEvents.addToWishlist:
			gtagEventName = gtagEvents.addToWishlist;
			break;
		case legacyCartEvents.remove:
			gtagEventName = gtagEvents.removeFromCart;
			break;
		case 'tire_experience_exit':
		case 'vehicle_picker_initialize':
			gtagEventName = eventName;
			break;
		default:
			gtagEventName = tracking.google.use_gtag_admind_analytics ? eventName : '';
	}

	return gtagEventName;
};

track.get_equivalent_visualizer_gtag_event = function(eventName) {
	let gtagEventName = null;

	switch (eventName) {
		case legacyVisualizerEvents.click:
			gtagEventName = gtagEvents.visualizerView;
			break;
		case legacyVisualizerEvents.launchError:
			gtagEventName = gtagEvents.visualizerError;
			break;
		default:
			gtagEventName = tracking.google.use_gtag_admind_analytics?eventName:'';
	}

	return gtagEventName;
};

module.exports = track;
